import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AuditFilters,
  type AuditFiltersState,
} from "@/components/audit/AuditFilters";
import { AuditEventDrawer } from "@/components/audit/AuditEventDrawer";
import { AuditTable } from "@/components/audit/AuditTable";
import { DEFAULT_AUDIT_FILTERS } from "@/components/audit/filter-defaults";
import { Button } from "@/components/ui/button";
import { isMissingEndpoint } from "@/lib/api-hooks";
import { useAuditList } from "@/lib/hooks/audit";
import type { AuditEvent, AuditListFilters } from "@/lib/schemas/audit";
import { useCurrentSession } from "@/lib/session";

export const Route = createFileRoute("/_app/audit")({
  component: AuditPage,
});

function AuditPage(): JSX.Element {
  const { t } = useTranslation();
  const session = useCurrentSession();
  const callerRole = session.data?.role;

  const [filters, setFilters] = useState<AuditFiltersState>(DEFAULT_AUDIT_FILTERS);
  const [cursor, setCursor] = useState<string | undefined>();
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | undefined>();

  const apiFilters: AuditListFilters = useMemo(() => {
    const base: AuditListFilters = { limit: 50, sort: "-occurred_at" };
    if (filters.category !== "all") base.category = filters.category;
    if (filters.outcome !== "all") base.outcome = filters.outcome;
    if (filters.severity !== "all") base.severity = filters.severity;
    if (filters.event_type) base.event_type = filters.event_type;
    if (filters.from) base.from = toIso(filters.from);
    if (filters.to) base.to = toIso(filters.to);
    if (cursor) base.cursor = cursor;
    return base;
  }, [filters, cursor]);

  const query = useAuditList(apiFilters);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            {t("nav.audit")}
          </h1>
          {query.data?.total_estimate !== undefined && (
            <p className="text-sm text-muted-foreground">
              {t("audit.total", { count: query.data.total_estimate })}
            </p>
          )}
        </div>
      </header>

      <AuditFilters
        value={filters}
        onChange={(next) => {
          setFilters(next);
          setCursor(undefined);
        }}
      />

      {query.error && (
        <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
          {isMissingEndpoint(query.error) ? (
            <p className="text-warning">{t("audit.endpoint_unavailable")}</p>
          ) : (
            <p className="text-destructive">
              {t("audit.load_error")} — {query.error.message}
            </p>
          )}
        </div>
      )}

      <AuditTable
        events={query.data?.data ?? []}
        isLoading={query.isLoading}
        onRowClick={(e) => setSelectedEvent(e)}
        selectedId={selectedEvent?.id}
      />

      {query.data && (query.data.next_cursor || query.data.prev_cursor) && (
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.prev_cursor}
            onClick={() => setCursor(query.data.prev_cursor ?? undefined)}
          >
            ← {t("common.back")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.next_cursor}
            onClick={() => setCursor(query.data.next_cursor ?? undefined)}
          >
            {t("common.next")} →
          </Button>
        </div>
      )}

      <AuditEventDrawer
        event={selectedEvent}
        onClose={() => setSelectedEvent(undefined)}
        callerRole={callerRole}
      />
    </div>
  );
}

/**
 * Convert `<input type="datetime-local">` value (local naive `YYYY-MM-DDTHH:mm`)
 * to ISO 8601 with local timezone. Backend Phase 3 stores TIMESTAMPTZ in UTC,
 * accetta qualunque ISO 8601 con offset esplicito.
 */
function toIso(localValue: string): string {
  if (!localValue) return localValue;
  try {
    return new Date(localValue).toISOString();
  } catch {
    return localValue;
  }
}
